import { useState } from 'react';
import axiosInstance from '../utils/axiosInstance';
import { usePagination } from './pagination';

export const useBeneficiaries = (perPage = 4) => {
    const [error, setError] = useState(null);
    const { 
        pagination, 
        loading, 
        fetchData: refreshBeneficiaries, 
        filters, 
        setFilters 
    } = usePagination('/api/beneficiaries', perPage);

    // Access beneficiaries directly from pagination.data
    const beneficiaries = pagination.data;

    const addBeneficiary = async (beneficiaryData) => {
        setError(null);
        try {
            const response = await axiosInstance.post('/api/beneficiaries', beneficiaryData);
            // Refresh the beneficiaries list after adding a new one
            await refreshBeneficiaries();
            return response.data.data;
        } catch (err) {
            console.error('Error adding beneficiary:', err);
            setError(err.response?.data?.message || 'Failed to add beneficiary');
            throw err;
        }
    };

    const editBeneficiary = async (id, beneficiaryData) => {
        setError(null);
        try {
            const response = await axiosInstance.put(`/api/beneficiaries/${id}`, beneficiaryData);
            // Refresh the beneficiaries list after editing
            await refreshBeneficiaries();
            return response.data.data;
        } catch (err) {
            console.error('Error editing beneficiary:', err);
            setError(err.response?.data?.message || 'Failed to edit beneficiary');
            throw err;
        }
    };

    const deleteBeneficiary = async (id) => {
        setError(null);
        try {
            await axiosInstance.delete(`/api/beneficiaries/${id}`);
            
            // Handle pagination when deleting the last item on a page
            const isLastItemOnPage = pagination.data.length === 1;
            const isNotFirstPage = pagination.current_page > 1;
            
            if (isLastItemOnPage && isNotFirstPage) {
                // Go to previous page
                setFilters({ 
                    ...filters, 
                    page: pagination.current_page - 1 
                });
            } else {
                // Just refresh the current page
                await refreshBeneficiaries();
            }
            
            return true;
        } catch (err) {
            console.error('Error deleting beneficiary:', err);
            setError(err.response?.data?.message || 'Failed to delete beneficiary');
            throw err;
        }
    };

    // Pagination handlers
    const handleNextPage = () => {
        if (pagination.current_page < Math.ceil(pagination.total / pagination.per_page)) {
            setFilters({ ...filters, page: pagination.current_page + 1 });
        }
    };

    const handlePreviousPage = () => {
        if (pagination.current_page > 1) {
            setFilters({ ...filters, page: pagination.current_page - 1 });
        }
    };

    const handlePageChange = (pageNumber) => {
        setFilters({ ...filters, page: pageNumber });
    };

    return {
        beneficiaries,
        pagination,
        loading,
        error,
        refreshBeneficiaries,
        addBeneficiary,
        editBeneficiary,
        deleteBeneficiary,
        handleNextPage,
        handlePreviousPage,
        handlePageChange,
        filters,
        setFilters
    };
}; 